import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Button } from '@/components/ui/Button';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { useNotification } from '@/hooks/common';
import { adminAPI } from '@/services/api';
import { User } from '@/types';
import { AUTO_APPROVAL, NOTIFICATION } from '@/constants';

interface UserSettingsTabProps {
  customer: User['customer'] | undefined;
}

export const UserSettingsTab: React.FC<UserSettingsTabProps> = ({ customer }) => {
  const { error, success, setError, setSuccess } = useNotification({ duration: NOTIFICATION.LONG_DURATION });
  const [autoApproval, setAutoApproval] = useState(false);
  const [autoApprovalDomains, setAutoApprovalDomains] = useState<string[]>(['']);
  const [originalAutoApproval, setOriginalAutoApproval] = useState(false);
  const [originalAutoApprovalDomains, setOriginalAutoApprovalDomains] = useState<string[]>(['']);
  const [maxAutoApprovalDomains, setMaxAutoApprovalDomains] = useState<number>(AUTO_APPROVAL.MAX_DOMAINS);
  const [loading, setLoading] = useState(false);

  // 초기값 로드
  useEffect(() => {
    if (!customer) return;

    const autoApprovalFromUser = customer.auto_approval ?? false;
    const domainsStr = customer.auto_approval_domains || '';
    const parsedDomains = domainsStr
      ? domainsStr.split(',').map((d: string) => d.trim()).filter((d: string) => d)
      : [];
    const normalizedDomains = parsedDomains.length > 0 ? parsedDomains.slice(0, AUTO_APPROVAL.MAX_DOMAINS) : [''];

    setAutoApproval(autoApprovalFromUser);
    setOriginalAutoApproval(autoApprovalFromUser);
    setAutoApprovalDomains(normalizedDomains);
    setOriginalAutoApprovalDomains(normalizedDomains);
  }, [customer]);

  // 시스템 설정에서 max_auto_approval_domains 값이 노출되는 시점에 연동 예정
  useEffect(() => {
    // TODO: SystemSettings에서 max_auto_approval_domains 조회 API가 준비되면 연동
    setMaxAutoApprovalDomains(AUTO_APPROVAL.MAX_DOMAINS);
  }, []);

  const effectiveMaxDomains = maxAutoApprovalDomains || AUTO_APPROVAL.MAX_DOMAINS;

  const handleDomainChange = (index: number, value: string) => {
    const next = [...autoApprovalDomains];
    next[index] = value;
    setAutoApprovalDomains(next);
  };

  const handleAddDomain = () => {
    if (autoApprovalDomains.length >= effectiveMaxDomains) return;
    setAutoApprovalDomains([...autoApprovalDomains, '']);
  };

  const handleRemoveDomain = (index: number) => {
    if (autoApprovalDomains.length <= 1) {
      setAutoApprovalDomains(['']);
      return;
    }
    const next = autoApprovalDomains.filter((_, i) => i !== index);
    setAutoApprovalDomains(next.length > 0 ? next : ['']);
  };

  const normalizeDomains = (domains: string[]) =>
    domains.map((d) => d.trim().toLowerCase()).filter((d) => d).join(',');

  const hasChanges =
    autoApproval !== originalAutoApproval ||
    normalizeDomains(autoApprovalDomains) !== normalizeDomains(originalAutoApprovalDomains);

  const handleSave = async () => {
    if (!customer) return;

    const normalizedDomains = autoApprovalDomains
      .map((d) => d.trim())
      .filter((d) => d)
      .slice(0, effectiveMaxDomains);

    // 검증 규칙:
    // - 수동 모드(autoApproval=false): 0~MAX_DOMAINS개 허용
    // - 자동 모드(autoApproval=true): 1~MAX_DOMAINS개 필수
    if (autoApproval && normalizedDomains.length === 0) {
      setError('자동 승인 모드에서는 최소 1개의 자동 승인 도메인이 필요합니다.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await adminAPI.updateMyCustomer({
        auto_approval: autoApproval,
        auto_approval_domains: normalizedDomains.length > 0 ? normalizedDomains.join(',') : undefined,
      });

      setSuccess('이용자 설정이 저장되었습니다.');
      setOriginalAutoApproval(autoApproval);
      setOriginalAutoApprovalDomains(normalizedDomains.length > 0 ? normalizedDomains : ['']);
    } catch (e) {
      setError('이용자 설정 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setAutoApproval(originalAutoApproval);
    setAutoApprovalDomains(originalAutoApprovalDomains.length > 0 ? originalAutoApprovalDomains : ['']);
    setError('');
    setSuccess('');
  };

  if (!customer) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>이용자 설정</CardTitle>
          <CardDescription>연결된 고객사가 없어 이용자 설정을 사용할 수 없습니다.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <AlertMessage
          type="error"
          message={error}
          onClose={() => setError('')}
        />
      )}
      {success && (
        <AlertMessage
          type="success"
          message={success}
          onClose={() => setSuccess('')}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>이용자 설정</CardTitle>
          <CardDescription>
            자동가입 / 수동가입 모드와 자동 승인 도메인을 설정합니다.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="autoApproval">자동 가입 모드</Label>
                <p className="text-xs text-gray-500">
                  자동가입을 활성화하면, 조건에 맞는 신규 사용자는 관리자 승인 없이 바로 활성화됩니다.
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Input
                  id="autoApproval"
                  type="checkbox"
                  className="w-4 h-4"
                  checked={autoApproval}
                  onChange={(e) => setAutoApproval(e.target.checked)}
                />
                <span className="text-sm text-gray-700">
                  {autoApproval ? '자동 승인' : '관리자 승인'}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>자동 승인 도메인 (선택 사항)</Label>
            <p className="text-xs text-gray-500">
              이메일 도메인이 아래 목록에 포함된 사용자는 자동으로 승인됩니다. 최대 {effectiveMaxDomains}개까지 설정할 수 있습니다.
            </p>

            <div className="space-y-2">
              {autoApprovalDomains.map((domain, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1 w-full">
                    <span className="w-6 text-xs text-gray-500 text-right">{index + 1}.</span>
                    <Input
                      value={domain}
                      onChange={(e) => handleDomainChange(index, e.target.value)}
                      placeholder="예: example.com"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemoveDomain(index)}
                    disabled={autoApprovalDomains.length === 1 && !domain}
                  >
                    삭제
                  </Button>
                </div>
              ))}
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddDomain}
              disabled={autoApprovalDomains.length >= effectiveMaxDomains}
            >
              도메인 추가
            </Button>
          </div>

          <div className="flex justify-end space-x-2 pt-2 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleReset}
              disabled={!hasChanges}
            >
              초기화
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={!hasChanges || loading}
            >
              {loading ? '저장 중...' : '저장'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
